import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils';
type SelectTriggerVariants = VariantProps<typeof selectTriggerVariants>;
export const selectTriggerVariants = cva(cn('justify-between relative flex w-full cursor-pointer select-none items-center gap-4xs rounded-sm border border-alternative px-sm'), {
  variants: {
    intent: {
      neutral: 'h-[48px] py-sm text-body-lg-medium',
      strong: 'h-[58px] py-md text-heading-sm-bold'
    },
    error: {
      true: 'border-transparent ring-1 ring-inset ring-destructive-default'
    },
    open: {
      true: 'border-transparent ring-2 ring-inset ring-strong-default'
    },
    disabled: {
      true: 'pointer-events-none bg-alternative',
      false: ''
    }
  },
  defaultVariants: {
    intent: 'neutral',
    error: false,
    disabled: false
  }
});
export type SelectTriggerProps = SelectTriggerVariants & {
  intent?: 'neutral' | 'strong';
  disabled?: boolean;
  trigger?: ReactNode;
};
export const SelectTrigger = forwardRef<ComponentRef<typeof SelectPrimitive.Trigger>, ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & SelectTriggerProps>(({
  className,
  children,
  intent,
  error,
  open,
  disabled,
  trigger,
  ...props
}, ref) => {
  if (trigger) {
    return <SelectPrimitive.Trigger {...props} asChild>
          {trigger}
        </SelectPrimitive.Trigger>;
  }
  return <SelectPrimitive.Trigger ref={ref} className={selectTriggerVariants({
    intent,
    error,
    open,
    disabled,
    className
  })} {...props}>
        {children}
      </SelectPrimitive.Trigger>;
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;